import JsonQuery from 'json-query';
import { uniqueId } from 'lodash';
import { useEffect, useState, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useLocalization } from '../../hooks';

interface FilterDropdownProps {
    data: any;
    label?: string;
    clearDropdownFilters?: boolean;
    placeholder?: string;
    dropdownDataKey?: string;
    displayLabelsKey?: string;
    dropdownType?: 'Single Select' | 'Multi Select';
    includeAllOption?: boolean;
    includeAllOptionLabel?: string;
    closeOnScroll?: boolean;
    dropdownItemSelectIconPosition?: 'Right' | 'Left';
    dropdownItemSelectIconType?: '' | 'FontAwesome Icon' | 'Image';
    dropdownItemSelectIcon?: string;
    dropdownItemSelectImage?: string;
    dropdownIconType?: '' | 'FontAwesome Icon' | 'Image';
    dropdownOpenIcon?: string;
    dropdownCloseIcon?: string;
    dropdownIconPosition?: 'Right' | 'Left';
    dropdownOpenImage?: string;
    dropdownCloseImage?: string;
    onSelectOption?: (opts: any, selectCurrentOption?: string) => void;
    selectedValue: string | null;
    cancelOptionClicks: number;
    canceledOption: any;
    resetClear: () => void;
    enableSearch?: boolean;
    searchPlaceholder?: string;
    noOptionFoundText?: string;
    showSearchIcon?: boolean;
    serachIconPosition?: string;
    searchIconType?: '' | 'FontAwesome Icon' | 'Image';
    searchIcon?: string;
    searchCancelIcon?: string;
    searchImageUrl?: string;
    searchCancelImageUrl?: string;
    hideDDIconSearch?: boolean;
    staticDataTabIndex?: number;
}

const FilterDropdown = (props: FilterDropdownProps) => {
    const { data, label, placeholder, dropdownDataKey, includeAllOption, ...rest } = props;
    const i18n = useLocalization();
    const [isOpen, setIsOpen] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef<any>(null);
    const dropdownToggleRef = useRef<any>(null);
    const searchInputRef = useRef<any>(null);
    const searchContainerRef = useRef<any>(null);
    const dropdownMenuRef = useRef<any>(null);
    const _placeholder: string = i18n.format(placeholder, data);
    const currentSelectionRef = useRef<any>(null);

    let selectedValue = rest.selectedValue
        ? rest?.dropdownType === 'Single Select'
            ? rest.selectedValue
            : rest.selectedValue.split('|')
        : [];

    let _options: any = dropdownDataKey
        ? Array.from(new Set(JsonQuery(dropdownDataKey as string, { data }).value || []))
        : [];

    const displayLabels = rest.displayLabelsKey
        ? JsonQuery(rest.displayLabelsKey, { data }).value
        : null;

    const includeAllOptionLabel = i18n.format(rest.includeAllOptionLabel, data) || 'All';

    if (includeAllOption) {
        _options = [includeAllOptionLabel, ..._options];
    }

    if (includeAllOptionLabel === rest.selectedValue && rest.dropdownType !== 'Single Select') {
        selectedValue = [..._options];
    }

    const [options, setOptions] = useState<any>(_options);

    const [selectedOptions, setSelectedOptions] = useState<any>(
        rest.selectedValue
            ? typeof selectedValue === 'string'
                ? [selectedValue]
                : [...selectedValue]
            : rest?.dropdownType === 'Single Select'
              ? includeAllOption
                  ? ['All']
                  : []
              : ''
    );

    const toggleDropdown = (isDDOpen: any) => {
        setIsOpen(isDDOpen);
        setShowSearch(!isOpen);
    };

    const clearSearchIcon = () => {
        setSearch('');
    };

    const clearSearchIconKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            setSearch('');
        }
    };

    const scrollToTop = () => {
        if (dropdownMenuRef?.current && dropdownMenuRef.current.scrollTo) {
            dropdownMenuRef.current.scrollTo({ top: 0 });
        }
    };

    const closeDropdown = () => {
        scrollToTop();
        setIsOpen(false);
        setSearch('');
        setShowSearch(false);
    };

    // handle if click outside of drodown and toggle icon
    const handleClickOutside = (e: any) => {
        const dropdownCond =
            dropdownRef?.current &&
            !dropdownRef.current.contains(e.target) &&
            dropdownToggleRef?.current &&
            !dropdownToggleRef.current.contains(e.target);
        const searchCond = searchInputRef?.current && !searchInputRef.current.contains(e.target);

        // check if click is outside od dropdown and toggle icon
        if (showSearch ? dropdownCond && searchCond : dropdownCond) {
            closeDropdown();
        }
    };

    // handle esc key event
    const handleEscape = (event: any) => {
        if (event.key === 'Escape') {
            closeDropdown();
        }
    };

    useEffect(() => {
        // event listener to check if click outside of component
        document.addEventListener('mousedown', handleClickOutside);
        // Escape key down event listener to close dropdown
        document.addEventListener('keydown', handleEscape);
        return () => {
            // remove the event listener
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownRef]);

    useEffect(() => {
        // This function will be called when the scroll event occurs
        const handleScroll = () => {
            if (rest?.closeOnScroll) {
                closeDropdown();
            }
        };

        // Add an event listener to the window object for the scroll event
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component is unmounted
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (rest?.canceledOption && selectedOptions?.length > 0) {
            const updatedOptions = [...selectedOptions];
            if (selectedOptions?.length > 0 && selectedOptions?.includes(includeAllOptionLabel)) {
                // check if all option is selected
                const allOptIndex = selectedOptions?.indexOf(includeAllOptionLabel);
                // remove all option from selected options
                updatedOptions?.splice(allOptIndex, 1);
            }

            const selectedIndex = updatedOptions.indexOf(rest?.canceledOption);
            updatedOptions?.splice(selectedIndex, 1);
            setSelectedOptions(updatedOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest?.canceledOption, rest?.cancelOptionClicks]);

    useEffect(() => {
        if (rest.clearDropdownFilters) {
            setSelectedOptions([]);
            rest.resetClear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest.clearDropdownFilters]);

    useEffect(() => {
        let _selectedOptions = selectedOptions;
        if (
            _selectedOptions?.length > 0 &&
            _options.filter(
                (option: any) =>
                    option !== includeAllOptionLabel && !_selectedOptions.includes(option)
            ).length === 0 &&
            !_selectedOptions.includes(includeAllOptionLabel)
        ) {
            setSelectedOptions([includeAllOptionLabel, ..._selectedOptions]);
            _selectedOptions = [includeAllOptionLabel, ..._selectedOptions];
        }

        const selectedOpts =
            rest?.dropdownType === 'Multi Select' &&
            includeAllOption &&
            _selectedOptions?.length > 0 &&
            _selectedOptions[0] === includeAllOptionLabel
                ? _selectedOptions.slice(1)
                : _selectedOptions;

        rest.onSelectOption && rest.onSelectOption(selectedOpts, currentSelectionRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptions]);

    useEffect(() => {
        const filteredOptions = _options?.filter((option: any) =>
            option?.toLowerCase()?.includes(search?.toLowerCase())
        );
        setOptions(filteredOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const handleOptionClick = (option: any) => {
        currentSelectionRef.current = option;
        if (rest?.dropdownType === 'Multi Select') {
            if (includeAllOption && option === includeAllOptionLabel) {
                if (
                    selectedOptions?.length > 0 &&
                    selectedOptions?.includes(includeAllOptionLabel)
                ) {
                    setSelectedOptions([]);
                } else {
                    // assign all default options as selected options
                    setSelectedOptions([..._options]);
                }
            } else {
                const updatedOptions = [...selectedOptions];
                if (
                    selectedOptions?.length > 0 &&
                    selectedOptions?.includes(includeAllOptionLabel)
                ) {
                    const allOptIndex = selectedOptions.indexOf(includeAllOptionLabel);
                    updatedOptions.splice(allOptIndex, 1);
                }

                const selectedIndex = updatedOptions.indexOf(option);
                if (selectedIndex === -1) {
                    setSelectedOptions([...updatedOptions, option]);
                } else {
                    updatedOptions.splice(selectedIndex, 1);
                    setSelectedOptions(updatedOptions);
                }
            }
        } else {
            setSelectedOptions([option]);
        }

        if (rest?.dropdownType === 'Single Select') {
            toggleDropdown(false);
        }
    };

    const renderIcon = () => {
        return (
            <>
                <>
                    {!(search.length > 0 && rest?.hideDDIconSearch) &&
                        (rest?.dropdownIconType === 'FontAwesome Icon' ? (
                            <i
                                className={`dropdown-toggle-icon fas ${
                                    isOpen ? rest.dropdownCloseIcon : rest?.dropdownOpenIcon
                                }`}
                            />
                        ) : rest?.dropdownIconType === 'Image' ? (
                            <img
                                className={'dropdown-toggle-image'}
                                src={isOpen ? rest?.dropdownCloseImage : rest?.dropdownOpenImage}
                                alt={
                                    isOpen && rest?.dropdownCloseImage
                                        ? 'dropdown-toggle-open'
                                        : rest?.dropdownOpenImage
                                          ? 'dropdown-toggle-close'
                                          : ''
                                }
                            />
                        ) : null)}
                </>
            </>
        );
    };

    const renderSearchIcon = () => {
        return (
            <>
                {rest?.searchIconType === 'FontAwesome Icon' ? (
                    <i
                        className={`dropdown-search-image fas ${
                            isOpen && search.length > 0 ? rest?.searchCancelIcon : rest?.searchIcon
                        }`}
                        onClick={isOpen && search.length > 0 ? clearSearchIcon : () => null}
                        onKeyDown={
                            isOpen && search.length > 0 ? clearSearchIconKeyDown : () => null
                        }
                    />
                ) : rest?.searchIconType === 'Image' ? (
                    <img
                        className={'dropdown-search-image'}
                        src={
                            isOpen && search.length > 0
                                ? rest?.searchCancelImageUrl
                                : rest?.searchImageUrl
                        }
                        alt={
                            isOpen && search.length > 0 && rest?.searchCancelImageUrl
                                ? 'dropdown-search-close'
                                : rest?.searchImageUrl
                                  ? 'dropdown-search'
                                  : ''
                        }
                        onClick={isOpen && search.length > 0 ? clearSearchIcon : () => null}
                        onKeyDown={
                            isOpen && search.length > 0 ? clearSearchIconKeyDown : () => null
                        }
                    />
                ) : null}
            </>
        );
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>, option: string) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.click();
            handleOptionClick(option);
        }
    };

    const handleDropdownSearchKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Escape') {
            toggleDropdown(false);
        } else if (event.key === ' ') {
            // Prevent default behavior of spacebar
            event.preventDefault();
            // Add a space to the search input
            setSearch((prevSearch) => prevSearch + ' ');
        }
    };

    const renderCheckbox = (option: any, id?: string) => {
        return (
            <>
                <input
                    type="checkbox"
                    id={id || option}
                    checked={selectedOptions.indexOf(option) !== -1}
                    onChange={() => handleOptionClick(option)}
                    onKeyDown={(e) => handleKeyDown(e, option)}
                />
            </>
        );
    };

    const handleDDKeyDown = (e: React.KeyboardEvent) => {
        const options: any = Array.from(
            document.querySelectorAll('.filter-dropdown [role="option"]')
        );
        const currentIndex = options.findIndex((option: any) => option === document.activeElement);

        if (e.key === 'ArrowDown') {
            // Prevent default scrolling
            e.preventDefault();
            const nextIndex: any = (currentIndex + 1) % options.length;
            options?.[nextIndex]?.focus();
        } else if (e.key === 'ArrowUp') {
            // Prevent default scrolling
            e.preventDefault();
            const prevIndex: any = (currentIndex - 1 + options.length) % options.length;
            options[prevIndex]?.focus();
        } else if (e.key === 'Tab' && currentIndex !== -1) {
            // Prevent default tabbing out
            e.preventDefault();
            const nextIndex: any = (currentIndex + 1) % options.length;
            options[nextIndex]?.focus();
        }
    };

    const labelId = uniqueId('dropdown-label-');

    return (
        <>
            {label ? (
                <div className="dropdown-label" id={labelId} tabIndex={rest.staticDataTabIndex}>
                    {i18n.format(label, data)}
                </div>
            ) : null}
            <Dropdown
                className="filter-dropdown"
                ref={dropdownRef}
                show={isOpen}
                onToggle={(isOpen, e: any) => {
                    if (!isOpen) {
                        scrollToTop();
                    }

                    if (rest?.enableSearch) {
                        if (
                            searchContainerRef?.current &&
                            !searchContainerRef?.current?.contains(e.target) &&
                            e.key !== 'Tab'
                        ) {
                            toggleDropdown(isOpen);
                        }
                        if (!showSearch && e.key !== 'Tab') toggleDropdown(true);
                    } else {
                        toggleDropdown(isOpen);
                    }
                }}
                onKeyDown={handleDDKeyDown}
            >
                <Dropdown.Toggle ref={dropdownToggleRef} aria-labelledby={labelId}>
                    {rest?.dropdownIconPosition === 'Left' && renderIcon()}
                    {rest?.dropdownType === 'Single Select' && selectedOptions[0] ? (
                        displayLabels ? (
                            displayLabels[selectedOptions[0]]
                        ) : (
                            selectedOptions[0]
                        )
                    ) : rest?.dropdownType === 'Multi Select' &&
                      rest?.enableSearch &&
                      showSearch ? (
                        <div ref={searchContainerRef} className="dropdown-search-container">
                            {rest?.serachIconPosition === 'Left' && renderSearchIcon()}
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="dropdown-search-input"
                                placeholder={i18n.format(rest?.searchPlaceholder, data)}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                tabIndex={0}
                                onKeyDown={handleDropdownSearchKeyDown}
                            />
                            {rest?.serachIconPosition === 'Right' && renderSearchIcon()}
                        </div>
                    ) : selectedOptions?.length > 0 ? (
                        `${
                            includeAllOption &&
                            selectedOptions.includes(rest?.includeAllOptionLabel)
                                ? selectedOptions[1]
                                : selectedOptions[0]
                        } ${selectedOptions?.length > 1 ? `+${selectedOptions.length - 1}` : ''}`
                    ) : (
                        <span className="placeholder">{_placeholder}</span>
                    )}
                    {rest?.dropdownIconPosition === 'Right' && renderIcon()}
                </Dropdown.Toggle>
                <Dropdown.Menu
                    className={`${
                        rest?.dropdownType === 'Single Select' ? 'single-select' : 'multi-select'
                    }`}
                    ref={dropdownMenuRef}
                >
                    {options && options?.length > 0 ? (
                        options?.map((option: any) => {
                            const optionWithUniqueId = uniqueId(`${option} - `);

                            return (
                                <div
                                    key={option}
                                    role="option"
                                    aria-selected={selectedOptions?.includes(option)}
                                    className={`dropdown-item ${
                                        selectedOptions?.includes(option) ? 'selected' : ''
                                    }`}
                                    tabIndex={rest.staticDataTabIndex}
                                    onKeyDown={(e) => handleKeyDown(e, option)}
                                    onClick={() =>
                                        rest?.dropdownType === 'Single Select' &&
                                        handleOptionClick(option)
                                    }
                                >
                                    {rest?.dropdownType === 'Multi Select' &&
                                        rest.dropdownItemSelectIconPosition === 'Left' &&
                                        renderCheckbox(option, optionWithUniqueId)}
                                    <label
                                        className="dropdown-item-label"
                                        htmlFor={
                                            rest?.dropdownType === 'Multi Select'
                                                ? optionWithUniqueId
                                                : ''
                                        }
                                    >
                                        {displayLabels ? displayLabels[option] : option}
                                    </label>
                                    {rest?.dropdownType === 'Multi Select' &&
                                        rest.dropdownItemSelectIconPosition === 'Right' &&
                                        renderCheckbox(option, optionWithUniqueId)}
                                </div>
                            );
                        })
                    ) : rest?.dropdownType === 'Multi Select' && rest?.noOptionFoundText ? (
                        <span className="no-option-found">
                            {i18n.format(rest.noOptionFoundText, data)}
                        </span>
                    ) : (
                        ''
                    )}
                </Dropdown.Menu>
            </Dropdown>
        </>
    );
};

export default FilterDropdown;
